import { FilterFn, Row } from "@tanstack/react-table";

// Row-level filter predicates plus the shared constants/helpers they rely on. Kept free of JSX so both
// the presentational filter components and the ReactTable wrapper can import them without pulling in React.

// Sentinel for null/undefined/empty cell values in multi-select filters (shown as "(Blank)" in the UI).
export const BLANK_FILTER_VALUE = "__BLANK__";
export const BLANK_FILTER_LABEL = "(Blank)";

// Cap how many checkbox rows are rendered at once. High-cardinality columns (price, quantity,
// settlement amount) can have thousands of distinct values; rendering them all as menu items is what
// makes the dropdown slow to open. Users narrow the rest with the search box.
export const MAX_RENDERED_FILTER_OPTIONS = 100;

export const isBlankCellValue = (value: unknown): boolean => value === null || typeof value === "undefined" || value === "";

export const toFilterOptionKey = (value: unknown): string => (isBlankCellValue(value) ? BLANK_FILTER_VALUE : String(value));

export const toFilterOptionLabel = (key: string): string => (key === BLANK_FILTER_VALUE ? BLANK_FILTER_LABEL : key);

// Shared by multiSelect and arrayMultiSelect, so the array filter can reuse the predicate for cells
// that turn out not to hold an array.
const matchesSelectedValue = (row: Row<any, any>, columnId: string, filterValue: string[]): boolean => {
    if (!filterValue || filterValue.length === 0) {
        return true; // Show all when nothing is selected
    }
    const value = row.getValue(columnId);
    const valueKey = toFilterOptionKey(value);
    return filterValue.map((v) => String(v)).includes(valueKey);
};

// filterFn: multiSelect - shows rows whose value matches any of the selected filter values.
// Understands the (Blank) sentinel so empty cells can be selected like any other value.
export const multiSelect: FilterFn<any, any> = (row: Row<any, any>, columnId: string, filterValue: string[]) =>
    matchesSelectedValue(row, columnId, filterValue);

// filterFn: arrayMultiSelect - like multiSelect but for array-valued cells (e.g. client names on a bond).
// A row matches when any selected value appears in the cell array, or when (Blank) is selected and the array is empty.
export const arrayMultiSelect: FilterFn<any, any> = (row: Row<any, any>, columnId: string, filterValue: string[]) => {
    if (!filterValue || filterValue.length === 0) {
        return true;
    }
    const value = row.getValue(columnId);
    if (!Array.isArray(value)) {
        return matchesSelectedValue(row, columnId, filterValue);
    }
    if (value.length === 0) {
        return filterValue.includes(BLANK_FILTER_VALUE);
    }
    return filterValue.some((selected) => {
        if (selected === BLANK_FILTER_VALUE) {
            return false;
        }
        return value.some((item) => toFilterOptionKey(item) === selected);
    });
};

// filterFn: notEquals
export const notEquals: FilterFn<any, any> = (row: Row<any, any>, columnId: string, filterValue: any) => {
    const value = row.getValue(columnId);
    return value != filterValue;
};

// filterFn: includesValueFormat, use with 'valueFormat' cell
export const includesValueFormat: FilterFn<any, any> = (row: Row<any, any>, columnId: string, filterValue: any) => {
    const value = (row.getValue(columnId) as any)?.toString();
    return value?.includes(filterValue);
};

// filterFn: numberEqualOrGreaterThan, use in numeric value column
export const numberEqualOrGreaterThan: FilterFn<any, any> = (row: Row<any, any>, columnId: string, filterValue: any) => {
    const search = Number(filterValue);
    const value = row.getValue(columnId) as any;
    return !!(value && value >= search);
};

// filterFn: startsWith
export const startsWith: FilterFn<any, any> = (row: Row<any, any>, columnId: string, filterValue: any) => {
    const value = (row.getValue(columnId) as any)?.toString();
    return !!(value && value.startsWith(filterValue));
};

// filterFn: numberStartsWith - prefix match on the number's own digits, so "10.2" matches 10.245 but
// not 25010.2 (unlike a substring search). Used for numeric columns (quantity, price, amounts). The
// special BLANK_FILTER_VALUE search matches only empty cells so users can find blank numbers.
export const numberStartsWith: FilterFn<any, any> = (row: Row<any, any>, columnId: string, filterValue: any) => {
    const search = String(filterValue ?? "").trim();
    if (search === "") {
        return true; // Show all when the search box is empty
    }
    const value = row.getValue(columnId);
    if (search === BLANK_FILTER_VALUE) {
        return isBlankCellValue(value); // "(Blank)" selected - keep only empty cells
    }
    if (isBlankCellValue(value)) {
        return false; // Blank cells never match a numeric prefix search
    }
    return String(value).startsWith(search);
};

// filterFn: notZero - filters out values that are 0 (number) or convert to 0 (string like "0.00")
export const notZero: FilterFn<any, any> = (row: Row<any, any>, columnId: string, filterValue: any) => {
    if (!filterValue || filterValue !== "0") {
        return true; // Show all if filter is not "Exclude 0"
    }
    const value = row.getValue(columnId);
    if (value === null || value === undefined || value === "") {
        return true; // Show null/undefined/empty values
    }
    const numValue = Number(value);
    if (isNaN(numValue)) {
        return true; // Non-numeric strings, NaN, etc.
    }
    return numValue !== 0;
};

// filterFn: zeroFilter - handles "All", "Exclude 0", and "Only 0" filter options
export const zeroFilter: FilterFn<any, any> = (row: Row<any, any>, columnId: string, filterValue: any) => {
    if (!filterValue || filterValue === "") {
        return true;
    }
    const value = row.getValue(columnId);
    if (value === null || value === undefined || value === "") {
        return filterValue !== "only0";
    }
    const numValue = Number(value);
    if (isNaN(numValue)) {
        return filterValue !== "only0";
    }
    const isZero = numValue === 0;
    if (filterValue === "0") {
        return !isZero;
    }
    if (filterValue === "only0") {
        return isZero;
    }
    return true;
};

// TanStack Table v9's built-in arrIncludes iterates the filter value as an array of needles.
// Select filters for array-of-ids columns pass a single string id, which v9 would treat as
// characters ("abc" -> "a", "b", "c") and match nothing — emptying the table.
export const arrIncludes: FilterFn<any, any> = (row: Row<any, any>, columnId: string, filterValue: any) => {
    const value = row.getValue(columnId);
    if (typeof value !== "string" && !Array.isArray(value)) {
        return false;
    }
    const needles = Array.isArray(filterValue) ? filterValue : [filterValue];
    if (Array.isArray(value)) {
        return needles.some((needle) => value.some((item) => String(item) === String(needle)));
    }
    return needles.some((needle) => (value as string).includes(needle));
};
(arrIncludes as any).autoRemove = (val: unknown) =>
    val === undefined || val === null || val === "" || (Array.isArray(val) && val.length === 0);

// The complete set of custom filter functions to register on the table. Referenced by name from the
// column defs (either declaratively via the filter registry or the legacy component mutations).
export const reactTableFilterFns: Record<string, FilterFn<any, any>> = {
    multiSelect,
    arrayMultiSelect,
    notEquals,
    includesValueFormat,
    numberEqualOrGreaterThan,
    notZero,
    zeroFilter,
    startsWith,
    numberStartsWith,
    arrIncludes
};
